---
title: Add skills to a persona
summary: Install skills from the library to teach a persona how to handle specific tasks.
visibility: authenticated
audience: author-admin
section: Advanced authoring
order: 96
last_verified: 2026-07-21
owner: docs
---

# Add skills to a persona

A **skill** is a reusable pack of instructions for a kind of task — for example, taking an
order or booking an appointment. You add skills on the **Skills** card in the
persona's [Workshop](/help/advanced/use-the-workshop) (Advanced mode).

## Two kinds of skill slots

The Skills card has two sections:

- **Default skills (always on)** — loaded into the persona's context on every turn. Use these
  for behavior the persona should always have.
- **Auto-load skills (loaded when relevant)** — intended to load only when a customer's
  message calls for them. *Note:* the auto-load classifier is enabled in a later release, so
  skills installed here are currently inert — for now, put skills you rely on under **Default
  skills**.

## Add one

Click **+ Add skill** in the section you want. The skill library opens; each entry shows its
name, version, a description, and any capabilities it **requires**. Click a skill to install
it. Skills you've already installed are hidden from the list.

## Skills and capabilities

A skill *declares* the capabilities it needs (shown as **Requires: …** on the skill row) but
doesn't wire them up. If a skill needs a capability you haven't bound as a tool, the Review
tab's wiring subsection flags it with a jump to the **Tools** card. So the usual flow is: add the
skill, then [bind the tools](/help/advanced/add-tools) it requires.

## Save

Adding or removing a skill stages a draft change. Click **Save** in the top bar to commit it
to the persona.

## See also

- [Add tools to a persona](/help/advanced/add-tools) — wire up the capabilities skills need.
- [Glossary](/help/reference/glossary) — *skill*, *capability*.
